import {
  FALLBACK_DOWNLOAD_BASE_URL,
  LEGACY_DOWNLOAD_BASE_URL,
  RELEASE_MANIFEST_CANDIDATES,
} from "./site.ts";

export type Platform = "windows" | "macos" | "linux";

export type ReleaseAsset = {
  platform: Platform;
  arch: string;
  file: string;
  size: number;
  sha256: string;
  url: string;
};

export type ReleaseManifest = {
  version: string;
  tag: string;
  releasedAt: string;
  assets: ReleaseAsset[];
};

export const platformNames: Record<Platform, string> = {
  windows: "Windows",
  macos: "macOS",
  linux: "Linux",
};

export function normalizeReleaseManifest(manifest: ReleaseManifest): ReleaseManifest {
  return {
    ...manifest,
    assets: manifest.assets.map((asset) => ({
      ...asset,
      url: asset.url.replace(LEGACY_DOWNLOAD_BASE_URL, FALLBACK_DOWNLOAD_BASE_URL),
    })),
  };
}

export function isReleaseManifest(value: unknown): value is ReleaseManifest {
  if (!value || typeof value !== "object") return false;
  const item = value as Partial<ReleaseManifest>;
  return (
    typeof item.version === "string" &&
    typeof item.tag === "string" &&
    typeof item.releasedAt === "string" &&
    Array.isArray(item.assets)
  );
}

export function detectPlatform(): Platform {
  if (typeof navigator === "undefined") return "windows";
  const userAgent = navigator.userAgent.toLowerCase();
  if (userAgent.includes("mac")) return "macos";
  if (userAgent.includes("linux")) return "linux";
  return "windows";
}

export function assetLabel(asset: ReleaseAsset) {
  if (asset.platform === "windows") return "Windows 安装程序";
  if (asset.platform === "macos" && asset.arch === "arm64") return "macOS Apple Silicon";
  if (asset.platform === "macos") return "macOS Intel";
  if (asset.file.endsWith(".deb")) return "Linux deb";
  if (asset.file.endsWith(".rpm")) return "Linux rpm";
  return "Linux AppImage";
}

async function fetchManifestFrom(url: string, signal: AbortSignal): Promise<ReleaseManifest> {
  const target = new URL(url, window.location.origin);
  target.searchParams.set("_", String(Date.now()));
  const response = await fetch(target.toString(), {
    cache: "no-store",
    headers: { "cache-control": "no-cache" },
    signal,
  });
  if (!response.ok) throw new Error(`manifest ${response.status}`);
  const value = (await response.json()) as unknown;
  if (!isReleaseManifest(value)) throw new Error("invalid manifest");
  return normalizeReleaseManifest(value);
}

export async function fetchReleaseManifest(signal: AbortSignal): Promise<ReleaseManifest> {
  let lastError: unknown;
  for (const candidate of RELEASE_MANIFEST_CANDIDATES) {
    try {
      return await fetchManifestFrom(candidate, signal);
    } catch (error) {
      if (signal.aborted) throw error;
      lastError = error;
    }
  }
  throw lastError instanceof Error ? lastError : new Error(String(lastError));
}
